/**
 * natural_orbit — a body's place on a Keplerian path about the star at the origin. PURE.
 * The system plane is XZ (y up), as the planets' are. Time is the WALL clock (seconds), so every client sees a comet
 * or a moon in the same place; nothing here is server state.
 */
import type { OrbitMakeup } from './natural_types.js';

export type V3 = [number, number, number];

/** Eccentric anomaly for mean anomaly `M` (rad) and eccentricity `e` < 1 (Newton; 8 steps hold 1e-9 to e = 0.97). */
export function eccentricAnomaly(M: number, e: number): number {
  const m = M - Math.floor(M / (Math.PI * 2)) * Math.PI * 2;
  let E = e < 0.8 ? m : Math.PI;
  for (let i = 0; i < 8; i++) E -= (E - e * Math.sin(E) - m) / (1 - e * Math.cos(E));
  return E;
}

/** The point of the orbit at mean anomaly `M` (rad; 0 = periapsis), in sector metres about the star. */
export function orbitPoint(o: OrbitMakeup, M: number, out: V3 = [0, 0, 0]): V3 {
  const a = (o.q + o.Q) / 2, e = (o.Q - o.q) / (o.Q + o.q);
  const E = eccentricAnomaly(M, e);
  // in the orbit's own plane: x toward periapsis
  const px = a * (Math.cos(E) - e), pz = a * Math.sqrt(1 - e * e) * Math.sin(E);
  // argument of periapsis (about y), inclination (about x), node (about y)
  const ca = Math.cos(o.arg), sa = Math.sin(o.arg);
  const x1 = px * ca - pz * sa, z1 = px * sa + pz * ca;
  const ci = Math.cos(o.inc), si = Math.sin(o.inc);
  const y2 = z1 * si, z2 = z1 * ci;
  const cn = Math.cos(o.node), sn = Math.sin(o.node);
  out[0] = x1 * cn - z2 * sn; out[1] = y2; out[2] = x1 * sn + z2 * cn;
  return out;
}

/** Mean anomaly (rad) at wall-clock second `tS`. */
export function meanAnomalyAt(o: OrbitMakeup, tS: number): number {
  return o.phase0 + (tS / o.periodS) * Math.PI * 2;
}

/** Where the body is at wall-clock second `tS`. */
export function orbitAt(o: OrbitMakeup, tS: number, out: V3 = [0, 0, 0]): V3 {
  return orbitPoint(o, meanAnomalyAt(o, tS), out);
}

/** A moon's offset from its planet's centre at wall-clock second `tS` (a circle of `orbitM`, tilted `inc` about `node`). */
export function moonOffsetAt(m: { orbitM: number; inc: number; node: number; periodS: number; phase0: number; sense: 1 | -1 }, tS: number, out: V3 = [0, 0, 0]): V3 {
  const th = m.phase0 + m.sense * (tS / m.periodS) * Math.PI * 2;
  const x = Math.cos(th) * m.orbitM, z = Math.sin(th) * m.orbitM;
  const ci = Math.cos(m.inc), si = Math.sin(m.inc);
  const y2 = z * si, z2 = z * ci;
  const cn = Math.cos(m.node), sn = Math.sin(m.node);
  out[0] = x * cn - z2 * sn; out[1] = y2; out[2] = x * sn + z2 * cn;
  return out;
}
